import React, { useMemo, useState, useEffect } from "react";
import Navbar from "../components/navbar";
import Footer from "../components/footer";
import Switcher from "../components/switcher";
import FaqContact from "../components/FaqContact";
import Seo from "../components/Seo";
import useStore from "../store/useStore";

const apiUrl = process.env.REACT_APP_STRAPI_URL;

// Landing de Preguntas y Respuestas (blog de Q&A)
export default function FaqLanding() {
  const [q, setQ] = useState("");
  const [categoria, setCategoria] = useState("");
  const categorias = useStore((s) => s.categorias);
  const fetchPWCCategories = useStore((s) => s.fetchPWCCategories);
  const pwcSearch = useStore((s) => s.pwcSearch);
  const searchPWCPreguntas = useStore((s) => s.searchPWCPreguntas);
  const loading = useStore((s) => s.loading);
  const [page, setPage] = useState(1);

    // SEO estático inicial (puede migrarse a Strapi)
    const seo = {
        metaTitle: "Conocimiento PwC - Preguntas y Respuestas",
        metaDescription:
        "Buscá respuestas sobre comercio exterior: normativa, exportaciones, importaciones y gravámenes.",
    };

  
    const video = `${apiUrl}/uploads/Dots_Background_10_1_7273030897.mp4`;
    const setVideoUrlError = useStore((state) => state.setVideoUrlError);

    // Utilidad: normaliza texto para búsqueda sin acentos ni mayúsculas
    const normalizeText = (s = "") =>
        s
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();

    // Resultados del backend (server-side)
  const resultados = useMemo(() => {
    const items = (pwcSearch?.data || []).map((p) => {
      return { 
        id: p.documentId, 
        pregunta: p.Pregunta, 
        resumen: p.RespuestaCorta || ""
      };
    });
    return items;
  }, [pwcSearch]);

  const hasQuery = q.trim().length >= 3 || Boolean(categoria);

    // Ejecutar búsqueda en Strapi cuando hay >=3 chars o categoría
    useEffect(() => {
      const query = q.trim();
      if (query.length >= 3 || categoria) {
        setPage(1); // reset paginado en nuevo criterio
        searchPWCPreguntas({
          q: query.length >= 3 ? query : undefined,
          categoriaId: categoria || undefined,
          page: 1,
          pageSize: 8,
        });
      }
    }, [q, categoria, searchPWCPreguntas]);

  // Cargar categorías desde Strapi
    useEffect(() => {
      fetchPWCCategories();
  }, [fetchPWCCategories]);

    // Selección por hash cuando existan categorías
    useEffect(() => {
      const hash = window.location.hash?.replace("#", "");
      if (!hash) return;
      if (categorias && categorias.find((c) => String(c.id) === String(hash))) {
        setCategoria(hash);
      }
    }, [categorias]);

  return (
    <>
      <Seo {...seo} />
      <header>
        <Navbar />
      </header>

      <main className="bg-sidom-light dark:bg-sidom-blue min-h-screen">
        {/* Hero de búsqueda */}
        <section className="relative overflow-hidden pt-36 pb-12 bg-sidom-blue text-sidom-light">
            <video
                className="absolute top-0 left-0 w-full h-full object-cover z-0"
                autoPlay
                loop
                muted
                onError={() => {
                    setVideoUrlError("No se pudo cargar el video");
                }}
                >
                <source src={video} type="video/mp4" />
            </video>
            <div className="container flex flex-col items-center text-center relative z-[1]">
                <div className="items-center gap-6 mb-6 flex flex-col">
                <img
                    src={`${process.env.REACT_APP_STRAPI_URL}/uploads/pwc_logo_neg_cde16cbb42.png`}
                    alt="PwC"
                    className="h-28 md:block"
                    onError={(e) => (e.currentTarget.style.display = "none")}
                />
                    <h1 className="text-2xl md:text-4xl font-extrabold">
                        La <span className="text-sidom-orange">respuesta</span> que necesita,
                        </h1>
                        <h1 className="text-2xl md:text-4xl font-extrabold">
                        desde la mirada <span className="text-sidom-orange">experta</span>
                    </h1>
                </div>

                <div className="flex w-full max-w-3xl gap-2">
                <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Buscá tu consulta sobre comercio exterior"
                    className="flex-1 rounded-full px-5 py-3 text-sidom-dark outline-none"
                />
                <button
                    onClick={() => window.scrollTo({ top: window.innerHeight, behavior: "smooth" })}
                    className="px-6 py-3 rounded-full bg-sidom-orange text-white font-semibold"
                >
                    BUSCAR
                </button>
                </div>

<div className="flex flex-wrap gap-3 mt-4">
          {(categorias || []).map((c) => {
            const hasCustomStyle = c.Background_Color && c.Font_Color;
            const customStyle = hasCustomStyle
              ? {
                  backgroundColor: c.Background_Color,
                  color: c.Font_Color,
                  ...(c.Font_Weight && { fontWeight: c.Font_Weight.toLowerCase() }),
                  ...(c.Font_Style && { fontStyle: c.Font_Style.toLowerCase() })
                }
              : {};
            const baseClasses = `px-4 py-2 rounded-full border transition ${
              String(categoria) === String(c.id)
                ? "bg-sidom-orange text-white border-sidom-orange"
                : "bg-white text-sidom-dark border-gray-300"
            }`;
            return (
              <button
                key={c.id}
                onClick={() => setCategoria(String(c.id) === String(categoria) ? "" : String(c.id))}
                className={baseClasses}
                style={customStyle}
              >
                {c.Categoria}
              </button>
            );
          })}
        </div>
            </div>
        </section>
        {/* Estado inicial: tarjetas informativas */}
        {!hasQuery && (
          <section className="container py-16">
            <div className="grid md:grid-cols-2 gap-10">
              {[
                "Conocimiento experto, claro y accesible.",
                "Diferencial real para clientes SIDOM.",
                "Preguntas frecuentes convertidas en valor.",
                "Canal de leads para consultoría.",
              ].map((txt, idx) => (
                <div
                  key={idx}
                  className="rounded-2xl p-10 text-center font-extrabold text-lg md:text-xl shadow-sm
                  bg-sidom-blue text-white
                  dark:border-white/10 dark:bg-white/5 dark:text-white"
                >
                  {txt}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Resultados solo cuando se busca o filtra */}
        {hasQuery && (
          <section className="container py-16">
            <h2 className="text-3xl md:text-4xl font-extrabold text-sidom-blue dark:text-sidom-light mb-8">
              Respuestas relacionadas
            </h2>

            {loading && (
              <div className="flex items-center gap-3 text-sidom-dark/70 dark:text-sidom-light/80 mb-6">
                <span className="inline-block h-5 w-5 rounded-full border-2 border-sidom-orange border-t-transparent animate-spin" />
                Buscando...
              </div>
            )}

            {!loading && resultados.length === 0 && (
              <p className="text-sidom-dark/70 dark:text-sidom-light/80">
                No encontramos resultados para tu búsqueda. Probá con otros términos
                o elegí una categoría.
              </p>
            )}

            {!loading && (
            <div className="grid md:grid-cols-2 gap-8">
              {resultados.map((f) => (
                <a
                  key={f.id}
                  href={`/conocimientopwc/${f.id}`}
                  className="block rounded-2xl border p-6 hover:shadow-lg transition
                  border-sidom-orange/60
                  dark:border-white/10 dark:bg-white/5"
                >
                  <h3 className="text-xl font-extrabold text-sidom-blue dark:text-sidom-light mb-3">
                    {f.pregunta}
                  </h3>
                  <p className="text-sidom-dark/80 dark:text-sidom-light/80">
                    {f.resumen}
                  </p>
                </a>
              ))}
            </div>
            )}
            {/* Paginación */}
            {hasQuery && !loading && (
              <div className="mt-10 flex items-center justify-between">
                <button
                  onClick={() => {
                    const pg = Math.max(1, (pwcSearch?.meta?.pagination?.page || 1) - 1);
                    setPage(pg);
                    const query = q.trim();
                    searchPWCPreguntas({
                      q: query.length >= 3 ? query : undefined,
                      categoriaId: categoria || undefined,
                      page: pg,
                      pageSize: pwcSearch?.meta?.pagination?.pageSize || 8,
                    });
                  }}
                  disabled={(pwcSearch?.meta?.pagination?.page || 1) <= 1}
                  className="px-4 py-2 rounded-full border disabled:opacity-50"
                >
                  Anterior
                </button>
                <div className="text-sm text-sidom-dark/70 dark:text-sidom-light/80">
                  Página {pwcSearch?.meta?.pagination?.page || 1} de {pwcSearch?.meta?.pagination?.pageCount || 1}
                </div>
                <button
                  onClick={() => {
                    const current = pwcSearch?.meta?.pagination?.page || 1;
                    const max = pwcSearch?.meta?.pagination?.pageCount || 1;
                    const pg = Math.min(max, current + 1);
                    setPage(pg);
                    const query = q.trim();
                    searchPWCPreguntas({
                      q: query.length >= 3 ? query : undefined,
                      categoriaId: categoria || undefined,
                      page: pg,
                      pageSize: pwcSearch?.meta?.pagination?.pageSize || 8,
                    });
                  }}
                  disabled={(pwcSearch?.meta?.pagination?.page || 1) >= (pwcSearch?.meta?.pagination?.pageCount || 1)}
                  className="px-4 py-2 rounded-full border disabled:opacity-50"
                >
                  Siguiente
                </button>
              </div>
            )}
          </section>
        )}

  {/* Formulario de contacto específico FAQ */}
  <FaqContact />
      </main>

      <Footer />
      <Switcher />
    </>
  );
}
